import axios from "axios";

import { Movement, SourceAttribute } from "@/hooks/useMovementsTable";

export interface GetSignedUrlParams {
  fileName: string;
  layoutId: string;
}

export type TargetStatus = "PROVISIONAL" | "FINAL";

export interface InitiateProvisionalUploadParams {
  sourceId: string;
  layoutId: string;
  fileId: string;
  originalFilename: string;
  targetStatus: TargetStatus;
  uniqueAttrs?: string[];
}

export interface InitiateProvisionalUploadResponse {
  status: string;
  version: string;
  uploadId: string;
  fileId: string;
  targetStatus: TargetStatus;
  message?: string;
}

export async function initiateProvisionalUpload({
  sourceId,
  layoutId,
  fileId,
  originalFilename,
  targetStatus,
  uniqueAttrs,
}: InitiateProvisionalUploadParams) {
  const { data } = await axios.post<InitiateProvisionalUploadResponse>(
    `/upload/provisional`,
    {
      sourceId,
      layoutId,
      fileId,
      originalFilename,
      targetStatus,
      uniqueAttrs,
    },
  );
  return data;
}

export interface ProvisionalDeltaStats {
  toDelete: number;
  toUpdate: number;
  toInsert: number;
  omitted: number;
}

export interface ProvisionalPreviewData {
  uploadId: string;
  movementsPreview: Movement[];
  sourceCols: SourceAttribute[];
  errorMessage: string | null;
  sourceId: string;
  tenantId: string;
  deltaStats: ProvisionalDeltaStats;
  updatable: boolean;
  warningMessage: string | null;
  allDuplicates: boolean;
}

export interface ProvisionalPreviewResponse {
  status: string;
  version: string;
  data: ProvisionalPreviewData;
  message?: string;
}

export async function getProvisionalPreview(uploadId: string) {
  const { data } = await axios.post<ProvisionalPreviewResponse>(
    `/upload/provisional/${uploadId}/preview`,
  );
  return data;
}

export interface ConfirmProvisionalUploadParams {
  uploadId: string;
  targetStatus: TargetStatus;
}

export interface OperationLogEntry {
  operation: "delete" | "update" | "insert";
  count: number;
}

export interface ConfirmProvisionalUploadResponse {
  status: string;
  version: string;
  message: string;
  uploadId: string;
  summary: {
    deleted: number;
    updated: number;
    inserted: number;
  };
  operationLog: OperationLogEntry[];
}

export async function confirmProvisionalUpload({
  uploadId,
  targetStatus,
}: ConfirmProvisionalUploadParams) {
  const { data } = await axios.post<ConfirmProvisionalUploadResponse>(
    `/upload/provisional/${uploadId}/confirm`,
    {
      targetStatus,
    },
  );
  return data;
}

export interface GetSignedUrlResponse {
  status: string;
  data: string;
  filename?: string;
}

export async function getSignedUrl({ fileName, layoutId }: GetSignedUrlParams) {
  const { data } = await axios.post<GetSignedUrlResponse>(
    `/upload/upload/presign/${layoutId}`,
    {
      fileName,
    },
  );
  return data;
}

export interface UploadFileParams {
  file: File;
  signedUrl: string;
}

export async function uploadFile({ file, signedUrl }: UploadFileParams) {
  const client = axios.create();
  const { data } = await client.put(signedUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
  });

  return data;
}

export async function confirmUpload(uploadId: string, update: boolean) {
  return axios.post(`/upload/file/confirm/${uploadId}`, { update });
}

export interface GetUploadPreviewResponse {
  data: {
    allDuplicates: boolean;
    errorMessage: string | null;
    idxDuplicates: string;
    deltaStats?: Partial<UploadHistoryDeltaStats> | null;
    movementsPreview: Movement[];
    sourceCols: SourceAttribute[];
    sourceId: string;
    tenantId: string;
    updatable: boolean;
    uploadId: string;
    warningMessage: string | null;
  };
}

export async function getUploadPreview(uploadId: string) {
  return axios.get<GetUploadPreviewResponse>(
    `/upload/file/preview/${uploadId}`,
  );
}

export async function processBigFileUpload(uploadId: string) {
  return axios.post(`/upload/file/big-file/${uploadId}`);
}

export interface FileConfig {
  fileType: string;
  linesToSkip: number;
  linesToOmit: number;
  contentType: string;
  parserFunction: string | null;
  _id: string;
}

export interface LayoutAttribute {
  _id: string;
  name: string;
  origin: string;
  order: number;
  visible: boolean;
  editable: boolean;
  dataType: string;
  headerIndex: number;
  inputFormat?: Record<string, string>;
  outputFormat?: Record<string, string>;
  validations?: Record<string, string>;
  possibleValues?: string[];
}

export interface BackendLayout {
  _id: string;
  name: string;
  fileConfig?: FileConfig | FileConfig[];
  attr: LayoutAttribute[];
}

export interface BackendSourceLayout {
  _id: string;
  name: string;
  fileConfig?: FileConfig;
  layouts: BackendLayout[];
  allowProvisional?: boolean;
}

export interface GetLayoutsResponse {
  data: BackendSourceLayout[];
}

export async function getLayouts() {
  return axios.get<GetLayoutsResponse>(`/upload/sources/layouts`);
}

export interface UploadHistoryDeltaStats {
  toDelete: number;
  toUpdate: number;
  toInsert: number;
  omitted?: number;
}

export type UploadHistoryStatus =
  | "validating"
  | "review"
  | "uploading"
  | "completed"
  | "error";

export interface UploadHistoryItem {
  _id: string;
  sourceId?: string;
  layoutId?: string;
  originalFilename: string | null;
  createdAt?: string | null;
  confirmingTimestamp: string | null;
  processingTimestamp?: string | null;
  movementsCount?: number | null;
  fileSizeBytes?: number | null;
  status?: UploadHistoryStatus;
  targetStatus?: TargetStatus | null;
  isProvisionalFlow?: boolean | null;
  successful: boolean | null;
  deltaStats?: Partial<UploadHistoryDeltaStats> | null;
  warningMessage?: unknown | null;
  errorMessage?: unknown | null;
  userHash: string | null;
  userName?: string | null;
  userEmail?: string | null;
  sourceName?: string | null;
  layoutName?: string | null;
  canDelete?: boolean;
  movementsDeleted?: boolean;
  movementsDeletedAt?: string | null;
  movementsDeletedBy?: string | null;
}

export interface GetSourceUploadsParams {
  limit?: number;
  skip?: number;
  sourceId?: string;
  sort?: string;
}

export interface GetSourceUploadsData {
  total: number;
  limit: number;
  skip: number;
  uploads: UploadHistoryItem[];
}

export interface GetSourceUploadsResponse {
  status: string;
  version: string;
  data: GetSourceUploadsData;
}

export async function getSourceUploadHistory(
  sourceId: string,
  params: GetSourceUploadsParams = {},
) {
  const { limit = 20, skip = 0 } = params;
  const { data } = await axios.get<GetSourceUploadsResponse>(
    `/upload/sources/${sourceId}/uploads`,
    { params: { limit, skip } },
  );
  return data;
}

export type ListUploadedSourcesByTenantResponse = GetSourceUploadsResponse;

export async function listUploadedSourcesByTenant(
  params: GetSourceUploadsParams = {},
): Promise<ListUploadedSourcesByTenantResponse> {
  const { limit = 20, skip = 0, sourceId, sort } = params;
  const { data } = await axios.get<ListUploadedSourcesByTenantResponse>(
    `/upload/uploads`,
    {
      params: {
        limit,
        skip,
        ...(sourceId ? { sourceId } : {}),
        ...(sort ? { sort } : {}),
      },
    },
  );
  return data;
}

export interface CountUploadedSourcesByTenantResponse {
  status: string;
  version: string;
  data: {
    status: UploadHistoryStatus;
    count: number;
  };
}

export async function countUploadedSourcesByTenant(
  status: UploadHistoryStatus = "review",
): Promise<CountUploadedSourcesByTenantResponse> {
  const { data } = await axios.get<CountUploadedSourcesByTenantResponse>(
    `/upload/uploads/count`,
    {
      params: { status },
    },
  );
  return data;
}

export interface DeleteUploadMovementsResponse {
  status: "accepted";
  requestId: string;
  message: string;
  statusUrl: string;
}

interface DeleteMovementsStatusPending {
  status: "pending";
  requestId: string;
  startedAt: string;
}

interface DeleteMovementsStatusProcessing {
  status: "processing";
  progress: string;
}

interface DeleteMovementsStatusSuccess {
  status: "success";
  requestId: string;
  uploadId: string;
  totalProcessed: string;
  totalFailed: string;
  completedAt: string;
  elapsedTime: string;
}

interface DeleteMovementsStatusError {
  status: "error";
  errorMessage: string;
  completedAt: string;
}

export type DeleteMovementsStatusResponse =
  | DeleteMovementsStatusPending
  | DeleteMovementsStatusProcessing
  | DeleteMovementsStatusSuccess
  | DeleteMovementsStatusError;

export async function deleteUploadMovements(
  uploadId: string,
): Promise<DeleteUploadMovementsResponse> {
  const url = `/upload/uploads/${uploadId}/delete-movements`;
  const response = await axios.post<DeleteUploadMovementsResponse>(url);

  const { data } = response;
  return data;
}

export async function getDeleteMovementsStatus(
  requestId: string,
): Promise<DeleteMovementsStatusResponse> {
  const { data } = await axios.get<DeleteMovementsStatusResponse>(
    `/upload/uploads/delete-movements/${requestId}/status`,
  );
  return data;
}

export type DiscardUploadResponse =
  | { status: "success" }
  | { response: string };

export async function discardUpload(
  uploadId: string,
): Promise<DiscardUploadResponse> {
  const url = `/upload/uploads/${uploadId}/discard`;
  const response = await axios.patch<DiscardUploadResponse>(url);

  const { data } = response;
  return data;
}
